import { ComponentProps } from "react";
import { ImagePresets } from "@pages/Article/components/ArticleThumbnail/utils/enums";
import { useLoadMoreArticlesTranslated } from "@pages/ZhHans/hooks/useLoadMoreArticlesTranslated";
import Card from "@particles/card/Card";
import TemplateG from "@particles/card/templates/TemplateG/TemplateG";
import TemplateH from "@particles/card/templates/TemplateH/TemplateH";
import { CardVariations } from "@particles/card/utils/types";
import { cn } from "@util/helpers";

type ArticleListProps = {
  /** This is the classname of the component. */
  className?: string;
  /** The card props to use for the articles. */
  cardProps: ComponentProps<typeof Card>[];
};

/**
 * Article List component to display the articles on the zh-hans page.
 * FIGMA: https://www.figma.com/design/rR8akVWS3SksIyaOSqetu4/WEB-BAU-2025?node-id=16097-26973&t=QZPAjkqVKrSzpWQN-4
 * */
const ArticleList = ({
  className,
  cardProps,
}: ArticleListProps): React.ReactElement => {
  const { ref, loadMoreData } = useLoadMoreArticlesTranslated({
    initialList: cardProps,
    size: 10,
  });

  const firstArticle = cardProps.slice(0, 1);
  const remainingArticles = cardProps.slice(1);

  const firstArticleVariant: CardVariations = {
    kicker: {
      color: cn("text-gray-515"),
      extraClass: cn("font-poppins text-4xs font-light leading-6"),
    },
    image: {
      width: "w-1/2",
      position: "left",
      aspectRatio: "aspect-3x2",
      orientation: "landscape",
      filters: { w: ImagePresets.MEDIUM, h: 320, dpr: 1, f: "webp" },
      extraClass: "hidden lg:flex",
    },
    content: {
      width: "w-1/2",
      extraClass: cn(
        "items-center self-stretch bg-gray-850 p-4 text-center text-white",
        "lg:items-start lg:bg-transparent lg:pb-0 lg:pr-0 lg:pt-0 lg:text-left lg:text-gray-850"
      ),
      blurb: {
        extraClass: cn(
          "text-gray-850 font-notoSansSC text-sm font-normal leading-relaxed tracking-2%"
        ),
        wrapperClassName: cn("lg:my-2"),
      },
      updatedTimeColor: cn("text-gray-850"),
      updatedTimeExtraClass: cn("font-poppins text-7xs"),
      updatedTimeWrapperClass: cn("mt-2 lg:mt-auto"),
    },
    title: {
      size: "text-4xl lg:text-6xl",
      color: "text-white lg:text-gray-850",
      extraClass: cn(
        "font-bold font-notoSansSC leading-tight tracking-2% [&>a]:underline-offset-[3px]"
      ),
    },
    subscriberLabel: { extraClass: "mt-2 mb-0" },
  };

  const variant: CardVariations = {
    kicker: {
      color: cn("text-gray-515"),
      extraClass: cn("font-poppins text-4xs font-light leading-6"),
    },
    image: {
      width: "w-1/4",
      position: "right",
      aspectRatio: "aspect-3x2",
      orientation: "landscape",
      filters: { w: ImagePresets.MEDIUM, h: 600, dpr: 1, f: "webp" },
    },
    content: {
      width: "w-3/4",
      extraClass: "pr-4",
      blurb: {
        extraClass: cn(
          "text-gray-850 font-notoSansSC text-sm font-normal leading-relaxed tracking-2%"
        ),
        wrapperClassName: cn("lg:my-2"),
      },
      updatedTimeColor: cn("text-gray-850"),
      updatedTimeExtraClass: cn("font-poppins text-7xs"),
      updatedTimeWrapperClass: cn("mt-2 lg:mt-auto"),
    },
    title: {
      size: cn("text-xl"),
      color: "text-gray-850",
      extraClass: cn(
        "font-bold font-notoSansSC leading-tight tracking-2% [&>a]:underline-offset-[3px]"
      ),
    },
    subscriberLabel: { extraClass: "mt-2 mb-0" },
  };

  return (
    <div data-testid="article-list-component" className={className}>
      <TemplateH
        cardProps={firstArticle}
        cardVariantOverride={firstArticleVariant}
        className="pb-4"
      />

      <TemplateG
        cardProps={[...remainingArticles, ...loadMoreData]}
        cardVariantOverride={variant}
        className="pt-4"
        lang="sc"
      />

      <div data-testid="article-list-load-more-trigger" ref={ref} />
    </div>
  );
};

export default ArticleList;
